import axios from "axios";
import Cookies from "universal-cookie";
import { getTeamUuid } from "../utils/cookies";

const BASE = process.env.REACT_APP_API_BASE_URL;

function getToken() {
    const cookies = new Cookies();
    return cookies.get("token");
}

function authHeaders() {
    return { authorization: `Bearer ${getToken()}` };
}

function projectUrl(projectUuid, suffix = "") {
    return `${BASE}/projects/${projectUuid}/questionnaires${suffix}`;
}

/* ---------------------------------------------------------------- authoring */

export function listQuestionnaires(projectUuid) {
    return axios.get(projectUrl(projectUuid), {
        params: { team_uuid: getTeamUuid() },
        headers: authHeaders(),
    });
}

export function getQuestionnaire(projectUuid, uuid) {
    return axios.get(projectUrl(projectUuid, `/${uuid}`), {
        params: { team_uuid: getTeamUuid() },
        headers: authHeaders(),
    });
}

export function saveQuestionnaire(projectUuid, data) {
    return axios.post(projectUrl(projectUuid), { ...data, team_uuid: getTeamUuid() }, {
        headers: authHeaders(),
    });
}

export function deleteQuestionnaire(projectUuid, uuid) {
    return axios.delete(projectUrl(projectUuid, `/${uuid}`), {
        params: { team_uuid: getTeamUuid() },
        headers: authHeaders(),
    });
}

export function setQuestionnaireStatus(projectUuid, uuid, status) {
    return axios.post(projectUrl(projectUuid, `/${uuid}/status`), { status, team_uuid: getTeamUuid() }, {
        headers: authHeaders(),
    });
}

export function uploadConsentDocument(projectUuid, uuid, file) {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("team_uuid", getTeamUuid());
    return axios.post(projectUrl(projectUuid, `/${uuid}/consent-document`), formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    });
}

export function uploadEthicsDocument(projectUuid, uuid, file) {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("team_uuid", getTeamUuid());
    return axios.post(projectUrl(projectUuid, `/${uuid}/ethics-document`), formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    });
}

export function saveQuestion(projectUuid, uuid, data) {
    return axios.post(projectUrl(projectUuid, `/${uuid}/questions`), { ...data, team_uuid: getTeamUuid() }, {
        headers: authHeaders(),
    });
}

export function deleteQuestion(projectUuid, uuid, questionUuid) {
    return axios.delete(projectUrl(projectUuid, `/${uuid}/questions/${questionUuid}`), {
        params: { team_uuid: getTeamUuid() },
        headers: authHeaders(),
    });
}

export function reorderQuestions(projectUuid, uuid, order) {
    return axios.post(projectUrl(projectUuid, `/${uuid}/questions/reorder`), { order, team_uuid: getTeamUuid() }, {
        headers: authHeaders(),
    });
}

export function getQuestionnaireResponses(projectUuid, uuid) {
    return axios.get(projectUrl(projectUuid, `/${uuid}/responses`), {
        params: { team_uuid: getTeamUuid() },
        headers: authHeaders(),
    });
}

/** Compiles the transcript and starts the analysis pipeline. */
export function processQuestionnaire(projectUuid, uuid) {
    return axios.post(projectUrl(projectUuid, `/${uuid}/process`), { team_uuid: getTeamUuid() }, {
        headers: authHeaders(),
    });
}

/** Emails the public link to a list of participants. Active questionnaires only. */
export function inviteParticipants(projectUuid, uuid, emails) {
    return axios.post(projectUrl(projectUuid, `/${uuid}/invite`), { team_uuid: getTeamUuid(), emails }, {
        headers: authHeaders(),
    });
}

/* ------------------------------------------------------------------ public */
/* No auth header: these are answered by people who have no account.          */

export function getPublicQuestionnaire(token) {
    return axios.get(`${BASE}/public/questionnaires/${token}`);
}

export function checkQuestionnairePassword(token, password) {
    return axios.post(`${BASE}/public/questionnaires/${token}/check-password`, { password });
}

export function startQuestionnaireResponse(token, data) {
    return axios.post(`${BASE}/public/questionnaires/${token}/start`, data);
}

export function submitAnswer(responseUuid, data) {
    return axios.post(`${BASE}/public/questionnaires/responses/${responseUuid}/answer`, data);
}

/**
 * DEPRECATED single-shot voice upload.
 *
 * Kept only so a respondent holding an older cached bundle mid-session still
 * works. New recordings stream through the chunked recording endpoints, which
 * are resumable and have no duration cap.
 */
export function submitVoiceAnswer(responseUuid, questionUuid, blob, duration) {
    const formData = new FormData();
    formData.append("question_uuid", questionUuid);
    formData.append("audio", blob, blob.type.includes("mp4") ? "answer.mp4" : "answer.webm");
    if (duration != null) formData.append("duration", Math.round(duration));
    return axios.post(`${BASE}/public/questionnaires/responses/${responseUuid}/answer/voice`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
}

export function completeQuestionnaireResponse(responseUuid) {
    return axios.post(`${BASE}/public/questionnaires/responses/${responseUuid}/complete`);
}

/* --------------------------------------------------------------- recording */

/**
 * Open a recording server-side. `questionUuid` is required for kind="voice"
 * (a spoken answer belongs to one question) and unused for screen/camera,
 * which cover the whole session.
 */
export function startRecording(responseUuid, kind, mimeType, questionUuid) {
    return axios.post(`${BASE}/public/questionnaires/responses/${responseUuid}/recordings`, {
        kind,
        mime_type: mimeType,
        ...(questionUuid ? { question_uuid: questionUuid } : {}),
    });
}

/**
 * One timeslice. Kept deliberately small and retried individually so a dropped
 * connection costs a few seconds of video rather than the whole session.
 */
export function uploadRecordingChunk(recordingUuid, index, blob, signal) {
    const formData = new FormData();
    formData.append("chunk_index", index);
    formData.append("chunk", blob, `chunk-${index}.webm`);
    return axios.post(`${BASE}/public/questionnaires/recordings/${recordingUuid}/chunk`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
    });
}

/**
 * Finalise a recording. `durationSeconds` is the length the client actually
 * measured; without it the server can only infer duration from the chunk count,
 * which rounds to the timeslice (a 15s answer would be recorded as 20s).
 */
export function completeRecording(recordingUuid, durationSeconds) {
    return axios.post(`${BASE}/public/questionnaires/recordings/${recordingUuid}/complete`,
        durationSeconds != null ? { duration: Math.round(durationSeconds) } : {});
}

/** Used on pagehide, where a normal request would be cancelled mid-flight. */
export function beaconCompleteRecording(recordingUuid) {
    const url = `${BASE}/public/questionnaires/recordings/${recordingUuid}/complete`;
    if (navigator.sendBeacon) {
        return navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
    }
    return false;
}
